import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileText,
  Trash2,
  Upload,
} from "lucide-react";
import { startTransition, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import {
  DOCUMENT_ACCEPT,
  deleteDocumentUpload,
  exportReadinessPackage,
  fetchDocumentUploads,
  preflightDocumentFile,
  reviewDocumentUpload,
  type DocumentReviewField,
  type DocumentUploadSummary,
  uploadDocument,
} from "../../../lib/documents";
import { ApiError } from "../../../lib/api";
import { cn } from "../../../lib/utils";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Dialog, DialogContent } from "../../ui/dialog";
import { Field, FieldShell } from "../../ui/field";
import { Input } from "../../ui/input";
import { PageHeader } from "../../ui/page-header";
import { Select } from "../../ui/select";
import { DocumentsListSkeleton } from "../../ui/skeleton";
import { useToast } from "../../ui/toast";

type ReviewDraftMap = Record<string, Record<string, string>>;
type FieldGroupKey = "income" | "assets" | "debt" | "education" | "details";
type DialogState =
  | { kind: "detail"; uploadID: string }
  | { kind: "export" }
  | { kind: "refresh" }
  | { kind: "upload" }
  | null;

const DOC_TYPE_OPTIONS = [
  { label: "Paystub", value: "paystub" },
  { label: "W-2", value: "w2" },
  { label: "Bank statement", value: "bank_statement" },
  { label: "Investment statement", value: "investment_statement" },
  { label: "Loan statement", value: "loan_statement" },
  { label: "Education document", value: "edu_cert" },
] as const;

export function DocumentsView({
  getToken,
  isLoaded,
  isSignedIn,
  onReadinessChanged,
}: {
  getToken: () => Promise<string | null>;
  isLoaded: boolean;
  isSignedIn: boolean;
  // SILL-255: invoked after a successful review/approval flow so the
  // shared readiness store refetches. Approving extracted fields writes
  // verified inputs (income, assets, etc.) into the profile and shifts
  // factor confidence — readiness MUST refresh. Upload-only and pure
  // browse interactions are not mutation events and do not call this.
  onReadinessChanged?: () => void;
}) {
  const { pushToast } = useToast();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [confirmDeleteUploadID, setConfirmDeleteUploadID] = useState<
    string | null
  >(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [draftDocType, setDraftDocType] = useState<string>("paystub");
  const [draftFile, setDraftFile] = useState<File | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportSelectedUploadIDs, setExportSelectedUploadIDs] = useState<
    string[]
  >([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewDraftsByUploadID, setReviewDraftsByUploadID] =
    useState<ReviewDraftMap>({});
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploads, setUploads] = useState<DocumentUploadSummary[]>([]);

  const sortedUploads = [...uploads].sort(
    (left, right) =>
      dateValue(lastUpdatedAt(right)) - dateValue(lastUpdatedAt(left)),
  );
  const selectedUpload =
    dialogState?.kind === "detail"
      ? (sortedUploads.find(
          (item) => item.upload.id === dialogState.uploadID,
        ) ?? null)
      : null;
  const detailUploadID =
    dialogState?.kind === "detail" ? dialogState.uploadID : null;
  const confirmDeleteUpload =
    typeof confirmDeleteUploadID === "string"
      ? (sortedUploads.find(
          (item) => item.upload.id === confirmDeleteUploadID,
        ) ?? null)
      : null;
  const dialogOpen =
    dialogState?.kind === "upload" ||
    dialogState?.kind === "export" ||
    (dialogState?.kind === "detail" && !!selectedUpload);
  const viewLoading = loading && isSignedIn;
  const selectedDrafts = selectedUpload
    ? (reviewDraftsByUploadID[selectedUpload.upload.id] ?? {})
    : {};
  const selectedGroups = selectedUpload
    ? groupFields(selectedUpload.fields, selectedUpload.upload.doc_type_hint)
    : [];
  const selectedStatus = selectedUpload
    ? documentStatus(selectedUpload).label
    : "";
  const verifiedExportInputs = buildVerifiedExportInputs(sortedUploads);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    if (!isSignedIn) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        startTransition(() => {
          setLoading(true);
          setLoadError(null);
        });

        const token = await getToken();
        if (!token) {
          throw new Error("Missing auth token");
        }

        const result = await fetchDocumentUploads(token);
        if (cancelled) {
          return;
        }

        startTransition(() => {
          setUploads(result.uploads);
          setReviewDraftsByUploadID((current) =>
            syncReviewDrafts(current, result.uploads),
          );
          setLoading(false);
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setLoadError(
          error instanceof Error ? error.message : "Unable to load documents.",
        );
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn]);

  const refreshDocuments = async (preferredUploadID?: string) => {
    const token = await getToken();
    if (!token) {
      throw new Error("Missing auth token");
    }

    const result = await fetchDocumentUploads(token);
    const preferredUpload =
      typeof preferredUploadID === "string"
        ? (result.uploads.find(
            (item) => item.upload.id === preferredUploadID,
          ) ?? null)
        : null;

    startTransition(() => {
      setUploads(result.uploads);
      setReviewDraftsByUploadID((current) =>
        syncReviewDrafts(current, result.uploads),
      );
    });

    return preferredUpload;
  };

  const openUploadDialog = () => {
    setDraftDocType("paystub");
    setDraftFile(null);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = "";
    }
    setDialogState({ kind: "upload" });
  };

  const openExportDialog = () => {
    setExportSelectedUploadIDs(sortedUploads.map((item) => item.upload.id));
    setDialogState({ kind: "export" });
  };

  const openDetailDialog = (upload: DocumentUploadSummary) => {
    if (detailUploadID === upload.upload.id) {
      setDialogState(null);
      return;
    }

    setReviewDraftsByUploadID((current) => ({
      ...current,
      [upload.upload.id]:
        current[upload.upload.id] ?? seedReviewDrafts(upload.fields),
    }));
    setDialogState({ kind: "detail", uploadID: upload.upload.id });
  };

  const updateDraftValue = (field: DocumentReviewField, nextValue: string) => {
    if (!selectedUpload || !field.observation_id) {
      return;
    }
    const observationID = field.observation_id;

    setReviewDraftsByUploadID((current) => ({
      ...current,
      [selectedUpload.upload.id]: {
        ...(current[selectedUpload.upload.id] ??
          seedReviewDrafts(selectedUpload.fields)),
        [observationID]: nextValue,
      },
    }));
  };

  const handleUpload = async () => {
    if (!draftFile) {
      pushToast({
        title: "Choose a file first",
        description: "Pick the document you want to upload.",
      });
      return;
    }

    const preflightError = preflightDocumentFile(draftFile);
    if (preflightError) {
      pushToast({ title: "Can't upload that file", description: preflightError });
      return;
    }

    setUploadBusy(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token");
      }

      const result = await uploadDocument(
        { docType: draftDocType, file: draftFile },
        token,
      );
      const uploadID = uploadedIDFromResult(result);
      const nextSelected = await refreshDocuments(uploadID);

      if (uploadInputRef.current) {
        uploadInputRef.current.value = "";
      }

      setDraftFile(null);
      setDialogState(
        nextSelected
          ? { kind: "detail", uploadID: nextSelected.upload.id }
          : null,
      );
    } catch (error) {
      pushToast({
        title: "Upload failed",
        description: mutationErrorDescription(
          error,
          "We couldn't upload that file. Try again in a moment.",
        ),
      });
    } finally {
      setUploadBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedUpload) {
      return;
    }

    const decisions = buildApprovalDecisions(selectedUpload, selectedDrafts);
    if (decisions.length === 0) {
      pushToast({
        title: "Nothing to approve",
        description: "This document does not have extracted inputs yet.",
      });
      return;
    }

    setReviewBusy(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token");
      }

      await reviewDocumentUpload(
        selectedUpload.upload.id,
        { decisions },
        token,
      );
      await refreshDocuments(selectedUpload.upload.id);
      // Approving decisions changes verified profile inputs, so the
      // sidebar readiness score should reflect the new factor signals.
      onReadinessChanged?.();
      setDialogState({ kind: "refresh" });
    } catch (error) {
      const description =
        error instanceof ApiError && error.status < 500
          ? error.message
          : "Something went wrong. Please try again.";
      pushToast({ title: "Review failed", description });
    } finally {
      setReviewBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteUpload) {
      return;
    }

    setDeleteBusy(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token");
      }

      await deleteDocumentUpload(confirmDeleteUpload.upload.id, token);
      await refreshDocuments();
      setConfirmDeleteUploadID(null);
      setDialogState(null);
      pushToast({
        title: "Document removed",
        description: confirmDeleteUpload.contributes_to_verified_snapshot
          ? "You can upload it again whenever you're ready. Refresh the page if you want the rest of the dashboard to update now."
          : "You can upload it again whenever you're ready.",
      });
    } catch (error) {
      pushToast({
        title: "Delete failed",
        description: mutationErrorDescription(
          error,
          "We couldn't remove that document. Try again in a moment.",
        ),
      });
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleExport = async () => {
    setExportBusy(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token");
      }

      const result = await exportReadinessPackage(
        { selectedUploadIDs: exportSelectedUploadIDs },
        token,
      );
      downloadBlob(result.blob, result.filename);
      setDialogState(null);
      pushToast({
        title: "Package ready",
        description: "Send the zip file to your lender or loan officer.",
      });
    } catch (error) {
      pushToast({
        title: "Export failed",
        description: mutationErrorDescription(
          error,
          "We couldn't build that package. Try again in a moment.",
        ),
      });
    } finally {
      setExportBusy(false);
    }
  };

  const toggleExportUpload = (uploadID: string, checked: boolean) => {
    setExportSelectedUploadIDs((current) => {
      if (checked) {
        return current.includes(uploadID) ? current : [...current, uploadID];
      }
      return current.filter((id) => id !== uploadID);
    });
  };

  return (
    <>
      <section className="space-y-6">
        <PageHeader
          title="Upload, review, and store your documents in one place."
          description="Start with a file, fix any extracted details, then keep the verified version in your list."
        />

        <section className="surface-card overflow-hidden bg-card">
          <div className="border-b border-border/80 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-foreground">
                  Uploaded documents
                </h2>
                <p className="text-sm text-muted-foreground">
                  Select a document to review extracted inputs or view locked
                  details.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:self-center">
                <Button className="rounded-pill" onClick={openUploadDialog}>
                  <Upload className="h-4 w-4" />
                  Upload document
                </Button>
                <Button
                  className="rounded-pill"
                  variant="outline"
                  onClick={openExportDialog}
                >
                  <Download className="h-4 w-4" />
                  Export package
                </Button>
              </div>
            </div>
          </div>

          {viewLoading ? (
            <DocumentsListSkeleton />
          ) : loadError ? (
            <SimpleState
              action={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Try again
                </Button>
              }
              description={loadError}
              icon={<AlertCircle className="h-5 w-5" />}
              title="We couldn't load your documents"
            />
          ) : sortedUploads.length === 0 ? (
            <SimpleState
              description="Upload your first document to start review."
              icon={<FileText className="h-5 w-5" />}
              title="No documents yet"
            />
          ) : (
            <div className="px-3 py-3 sm:px-4">
              <div className="hidden grid-cols-[minmax(0,1.5fr)_auto_auto] gap-4 px-3 py-2 md:grid">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Document
                </p>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Status
                </p>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Last updated
                </p>
              </div>

              <div className="space-y-2">
                {sortedUploads.map((item) => {
                  const status = documentStatus(item);
                  const selected = detailUploadID === item.upload.id;

                  return (
                    <button
                      key={item.upload.id}
                      type="button"
                      aria-haspopup="dialog"
                      onClick={() => openDetailDialog(item)}
                      className={cn(
                        "surface-card-interactive grid w-full gap-3 px-4 py-4 text-left md:grid-cols-[minmax(0,1.5fr)_auto_auto] md:items-center",
                        selected && "border-primary bg-primary/6",
                      )}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground">
                          {item.upload.original_name}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {docTypeLabel(item.upload.doc_type_hint)}
                        </p>
                      </div>

                      <div className="md:justify-self-start">
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </div>

                      <div className="text-sm text-muted-foreground md:justify-self-end">
                        <p className="md:text-right">
                          {formatDate(lastUpdatedAt(item))}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </section>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => !open && setDialogState(null)}
      >
        {dialogState?.kind === "upload" ? (
          <DialogContent
            className="sm:max-w-lg"
            description="Choose a document type and file to start review."
            title="Upload document"
          >
            <div className="space-y-5">
              <Select
                label="Document type"
                items={DOC_TYPE_OPTIONS.map((item) => ({
                  label: item.label,
                  value: item.value,
                }))}
                value={draftDocType}
                onValueChange={setDraftDocType}
              />

              <Field
                htmlFor="documents-upload-input"
                label="File"
                description="PDF, PNG, or JPEG, up to 20 MB"
              >
                <input
                  ref={uploadInputRef}
                  id="documents-upload-input"
                  type="file"
                  accept={DOCUMENT_ACCEPT}
                  className="sr-only"
                  onChange={(event) =>
                    setDraftFile(event.currentTarget.files?.[0] ?? null)
                  }
                />
                <FieldShell className="justify-between gap-3">
                  <p
                    className={cn(
                      "min-w-0 flex-1 truncate text-sm",
                      draftFile ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {draftFile?.name ?? "No file selected"}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    Choose file
                  </Button>
                </FieldShell>
              </Field>

              <Button
                className="w-full rounded-pill"
                loading={uploadBusy}
                onClick={handleUpload}
              >
                <Upload className="h-4 w-4" />
                Upload document
              </Button>
            </div>
          </DialogContent>
        ) : dialogState?.kind === "export" ? (
          <DialogContent
            className="sm:max-w-2xl"
            description="Choose the files to include. The zip includes a Sill-branded summary PDF and the raw documents you select."
            title="Export readiness package"
          >
            <div className="space-y-5">
              <SimpleReviewMessage>
                Download this zip file and send the full package to your lender
                or loan officer.
              </SimpleReviewMessage>

              <section className="space-y-3">
                <p className="eyebrow-label">Verified inputs</p>
                {verifiedExportInputs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    The PDF will include your current readiness summary. No
                    document-backed inputs are locked yet.
                  </p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {verifiedExportInputs.slice(0, 4).map((item) => (
                      <div
                        key={`${item.uploadID}-${item.label}`}
                        className="surface-muted px-3 py-2"
                      >
                        <p className="text-sm font-semibold text-foreground">
                          {item.label}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {item.documentName}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-foreground">
                    Supporting documents
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {exportSelectedUploadIDs.length} selected
                  </p>
                </div>

                {sortedUploads.length === 0 ? (
                  <SimpleReviewMessage>
                    No documents are available. You can still download the
                    Sill-branded summary PDF.
                  </SimpleReviewMessage>
                ) : (
                  <div className="space-y-3">
                    {sortedUploads.map((item) => {
                      const checked = exportSelectedUploadIDs.includes(
                        item.upload.id,
                      );
                      return (
                        <div
                          key={item.upload.id}
                          className="surface-card px-4 py-3"
                        >
                          <Checkbox
                            checked={checked}
                            label={item.upload.original_name}
                            description={
                              item.contributes_to_verified_snapshot
                                ? "Included in your verified view"
                                : docTypeLabel(item.upload.doc_type_hint)
                            }
                            onCheckedChange={(next) =>
                              toggleExportUpload(item.upload.id, next)
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button variant="outline" onClick={() => setDialogState(null)}>
                  Cancel
                </Button>
                <Button loading={exportBusy} onClick={handleExport}>
                  <Download className="h-4 w-4" />
                  Download package
                </Button>
              </div>
            </div>
          </DialogContent>
        ) : selectedUpload ? (
          <DialogContent
            className="sm:max-w-3xl"
            description={
              selectedUpload.review_state === "reviewed"
                ? "Verified inputs are locked."
                : selectedUpload.fields.length === 0
                  ? "Extracted inputs will appear here after upload."
                  : "Review the extracted inputs and approve when you're done."
            }
            title={selectedUpload.upload.original_name}
          >
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="neutral">
                    {docTypeLabel(selectedUpload.upload.doc_type_hint)}
                  </Badge>
                  <Badge variant={documentStatus(selectedUpload).variant}>
                    {selectedStatus}
                  </Badge>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    Last updated {formatDate(lastUpdatedAt(selectedUpload))}
                  </p>

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() =>
                      setConfirmDeleteUploadID(selectedUpload.upload.id)
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove document
                  </Button>
                </div>

                {selectedUpload.contributes_to_verified_snapshot ? (
                  <SimpleReviewMessage>
                    This document is part of your verified view right now. If
                    you remove it, refresh the rest of the dashboard afterward
                    so the change carries through.
                  </SimpleReviewMessage>
                ) : null}
              </div>

              {selectedUpload.fields.length === 0 ? (
                <SimpleReviewMessage>
                  Extracted inputs are not ready yet. You can come back to this
                  document from the main list.
                </SimpleReviewMessage>
              ) : selectedUpload.review_state === "reviewed" ? (
                <div className="space-y-4">
                  <SimpleReviewMessage tone="success">
                    This document is verified and its inputs are locked.
                  </SimpleReviewMessage>

                  {selectedGroups.map((group) => (
                    <section
                      key={group.key}
                      className="border-t border-border-soft pt-5 first:border-0 first:pt-0"
                    >
                      <p className="eyebrow-label">{group.title}</p>
                      <div className="mt-4 space-y-3">
                        {group.fields.map((field) => (
                          <LockedFieldRow
                            key={
                              field.observation_id ??
                              `${field.entity_key}-${field.field_key}`
                            }
                            field={field}
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedGroups.map((group) => (
                    <section
                      key={group.key}
                      className="border-t border-border-soft pt-5 first:border-0 first:pt-0"
                    >
                      <p className="eyebrow-label">{group.title}</p>
                      <div className="mt-4 space-y-3">
                        {group.fields.map((field) => (
                          <EditableField
                            key={
                              field.observation_id ??
                              `${field.entity_key}-${field.field_key}`
                            }
                            field={field}
                            uploadID={selectedUpload.upload.id}
                            value={draftValueForField(field, selectedDrafts)}
                            onChange={(value) => updateDraftValue(field, value)}
                          />
                        ))}
                      </div>
                    </section>
                  ))}

                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setDialogState(null)}
                    >
                      Edit later
                    </Button>
                    <Button loading={reviewBusy} onClick={handleApprove}>
                      <CheckCircle2 className="h-4 w-4" />
                      Approve
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        ) : null}
      </Dialog>

      <Dialog
        open={!!confirmDeleteUpload}
        onOpenChange={(open) => {
          if (!open && !deleteBusy) {
            setConfirmDeleteUploadID(null);
          }
        }}
      >
        {confirmDeleteUpload ? (
          <DialogContent
            className="sm:max-w-lg"
            description={
              confirmDeleteUpload.contributes_to_verified_snapshot
                ? "This file is helping power your verified view right now. If you continue, we’ll remove it and rebuild your current snapshot in the same step."
                : "This removes the file from your documents list. You can upload it again later."
            }
            title="Remove this document?"
          >
            <div className="space-y-4">
              <div className="rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-foreground">
                {confirmDeleteUpload.upload.original_name}
              </div>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDeleteUploadID(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  loading={deleteBusy}
                  onClick={handleDelete}
                >
                  <Trash2 className="h-4 w-4" />
                  Yes, remove it
                </Button>
              </div>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>

      <Dialog
        open={dialogState?.kind === "refresh"}
        onOpenChange={(open) => !open && setDialogState(null)}
      >
        <DialogContent
          className="sm:max-w-lg"
          description="Refresh the page so your approved document inputs carry through the rest of your dashboard."
          title="Refresh to update the rest of the page"
        >
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setDialogState(null)}>
              Later
            </Button>
            <Button onClick={() => window.location.reload()}>
              Refresh page
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EditableField({
  field,
  onChange,
  uploadID,
  value,
}: {
  field: DocumentReviewField;
  onChange: (value: string) => void;
  uploadID: string;
  value: string;
}) {
  const inputID = `documents-field-${uploadID}-${field.observation_id ?? field.field_key}`;
  const isAmount = valueKindForField(field) === "amount";

  return (
    <Input
      id={inputID}
      inputMode={isAmount ? "decimal" : undefined}
      label={field.label}
      startAdornment={isAmount ? <span>$</span> : undefined}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );
}

function LockedFieldRow({ field }: { field: DocumentReviewField }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border-soft pb-3 last:border-0 last:pb-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {field.label}
      </p>
      <p className="text-sm font-semibold text-foreground">
        {formatFieldDisplayValue(field, editableFieldValue(field))}
      </p>
    </div>
  );
}

function SimpleState({
  action,
  description,
  icon,
  title,
}: {
  action?: ReactNode;
  description: string;
  icon: ReactNode;
  title: string;
}) {
  return (
    <div className="flex min-h-72 flex-col items-center justify-center px-6 py-10 text-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </div>
      <p className="mt-4 text-base font-semibold text-foreground">{title}</p>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">
        {description}
      </p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

function SimpleReviewMessage({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "success";
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border px-4 py-3 text-sm",
        tone === "success"
          ? "border-success/25 bg-success/10 text-success-foreground"
          : "border-border bg-muted/60 text-muted-foreground",
      )}
    >
      {children}
    </div>
  );
}

function buildVerifiedExportInputs(uploads: DocumentUploadSummary[]) {
  return uploads.flatMap((upload) =>
    upload.contributes_to_verified_snapshot
      ? upload.fields
          .filter((field) => field.decision)
          .map((field) => ({
            documentName: upload.upload.original_name,
            label: field.label,
            uploadID: upload.upload.id,
          }))
      : [],
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function documentStatus(upload: DocumentUploadSummary) {
  if (upload.review_state === "reviewed") {
    return { label: "Verified", variant: "success" as const };
  }

  return { label: "Needs review", variant: "warning" as const };
}

function mutationErrorDescription(error: unknown, fallback: string) {
  if (!(error instanceof Error) || !error.message) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(error.message) as { error?: string };
    if (typeof parsed.error === "string" && parsed.error.trim() !== "") {
      return parsed.error;
    }
  } catch {
    // Fall through to the status-aware branch below.
  }

  const status =
    "status" in error && typeof error.status === "number" ? error.status : null;
  if (status !== null && status < 500) {
    return error.message;
  }

  return fallback;
}

function buildApprovalDecisions(
  upload: DocumentUploadSummary,
  drafts: Record<string, string>,
) {
  return upload.fields
    .filter(
      (
        field,
      ): field is DocumentReviewField & {
        observation_id: string;
      } =>
        typeof field.observation_id === "string" && field.observation_id !== "",
    )
    .map((field) => {
      const draftValue = draftValueForField(field, drafts);
      const changed =
        fieldValueSignature(field, draftValue) !==
        fieldValueSignature(field, editableFieldValue(field));

      if (changed) {
        return {
          decision: "edit" as const,
          edited_value: serializeFieldValue(field, draftValue),
          field_key: field.field_key,
          observation_id: field.observation_id,
        };
      }

      return {
        decision: "accept" as const,
        field_key: field.field_key,
        observation_id: field.observation_id,
      };
    });
}

function syncReviewDrafts(
  current: ReviewDraftMap,
  uploads: DocumentUploadSummary[],
): ReviewDraftMap {
  const next: ReviewDraftMap = {};

  for (const upload of uploads) {
    const seeded = seedReviewDrafts(upload.fields);
    const existing = current[upload.upload.id];

    next[upload.upload.id] =
      upload.review_state === "reviewed" || !existing
        ? seeded
        : { ...seeded, ...existing };
  }

  return next;
}

function seedReviewDrafts(fields: DocumentReviewField[]) {
  return Object.fromEntries(
    fields
      .filter(
        (
          field,
        ): field is DocumentReviewField & {
          observation_id: string;
        } =>
          typeof field.observation_id === "string" &&
          field.observation_id !== "",
      )
      .map((field) => [field.observation_id, editableFieldValue(field)]),
  );
}

function groupFields(fields: DocumentReviewField[], docTypeHint?: string) {
  const groups = new Map<FieldGroupKey, DocumentReviewField[]>();

  for (const field of fields) {
    const key = fieldGroupKey(field, docTypeHint);
    groups.set(key, [...(groups.get(key) ?? []), field]);
  }

  return Array.from(groups.entries()).map(([key, groupFields]) => ({
    fields: groupFields,
    key,
    title: fieldGroupLabel(key),
  }));
}

function fieldGroupKey(
  field: DocumentReviewField,
  docTypeHint?: string,
): FieldGroupKey {
  const source =
    `${field.entity_key} ${field.field_key} ${field.label}`.toLowerCase();

  if (
    source.includes("income") ||
    source.includes("employer") ||
    source.includes("pay")
  ) {
    return "income";
  }
  if (
    source.includes("asset") ||
    source.includes("account") ||
    source.includes("cash") ||
    source.includes("balance")
  ) {
    return "assets";
  }
  if (
    source.includes("debt") ||
    source.includes("loan") ||
    source.includes("liabil") ||
    source.includes("payment")
  ) {
    return "debt";
  }
  if (
    source.includes("education") ||
    source.includes("school") ||
    source.includes("student")
  ) {
    return "education";
  }

  return categoryForDocType(docTypeHint);
}

function fieldGroupLabel(group: FieldGroupKey) {
  switch (group) {
    case "income":
      return "Income";
    case "assets":
      return "Assets";
    case "debt":
      return "Debt";
    case "education":
      return "Education";
    case "details":
      return "Details";
  }
}

function categoryForDocType(docTypeHint?: string): FieldGroupKey {
  switch (docTypeHint) {
    case "paystub":
    case "w2":
      return "income";
    case "bank_statement":
    case "investment_statement":
      return "assets";
    case "loan_statement":
      return "debt";
    case "edu_cert":
      return "education";
    default:
      return "details";
  }
}

function draftValueForField(
  field: DocumentReviewField,
  drafts: Record<string, string>,
) {
  if (!field.observation_id) {
    return editableFieldValue(field);
  }

  return drafts[field.observation_id] ?? editableFieldValue(field);
}

function editableFieldValue(field: DocumentReviewField) {
  const value = field.decision?.edited_value ?? field.normalized_value;

  if (
    typeof value === "object" &&
    value !== null &&
    "amount" in value &&
    typeof value.amount === "number"
  ) {
    return String(value.amount);
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "value" in value &&
    typeof value.value === "string"
  ) {
    return value.value;
  }
  if (typeof field.display_value === "string" && field.display_value !== "") {
    return field.display_value.replace(/^\$/, "").replace(/,/g, "");
  }

  return "";
}

function formatFieldDisplayValue(
  field: DocumentReviewField,
  draftValue: string,
) {
  const value = draftValue.trim();

  if (value === "") {
    return "Not provided";
  }
  if (valueKindForField(field) === "amount") {
    const amount = Number.parseFloat(value.replace(/,/g, ""));
    if (Number.isFinite(amount)) {
      return new Intl.NumberFormat("en-US", {
        currency: "USD",
        maximumFractionDigits: 0,
        style: "currency",
      }).format(amount);
    }
  }

  return value;
}

function serializeFieldValue(field: DocumentReviewField, draftValue: string) {
  if (valueKindForField(field) === "amount") {
    const amount = Number.parseFloat(draftValue.replace(/,/g, ""));
    return { amount: Number.isFinite(amount) ? Math.round(amount) : 0 };
  }

  return { value: draftValue.trim() };
}

function fieldValueSignature(field: DocumentReviewField, draftValue: string) {
  if (valueKindForField(field) === "amount") {
    const amount = Number.parseFloat(draftValue.replace(/,/g, ""));
    return Number.isFinite(amount) ? `amount:${Math.round(amount)}` : "amount:";
  }

  return `text:${draftValue.trim()}`;
}

function valueKindForField(field: DocumentReviewField) {
  if (AMOUNT_FIELD_KEYS.has(field.field_key)) {
    return "amount";
  }
  const value = field.decision?.edited_value ?? field.normalized_value;
  if (typeof value === "object" && value !== null && "amount" in value) {
    return "amount";
  }
  return "text";
}

const AMOUNT_FIELD_KEYS = new Set([
  "available_balance",
  "current_balance",
  "ending_balance",
  "gross_annual_income",
  "gross_monthly_income",
  "minimum_payment_amount",
  "monthly_payment",
  "net_monthly_income",
  "statement_balance",
  "wages_tips_other_compensation",
]);

function uploadedIDFromResult(result: unknown) {
  if (
    typeof result === "object" &&
    result !== null &&
    "upload" in result &&
    typeof result.upload === "object" &&
    result.upload !== null &&
    "id" in result.upload
  ) {
    return String(result.upload.id);
  }

  return undefined;
}

function docTypeLabel(docTypeHint?: string) {
  return (
    DOC_TYPE_OPTIONS.find((item) => item.value === docTypeHint)?.label ??
    "Document"
  );
}

function lastUpdatedAt(upload: DocumentUploadSummary) {
  const timestamps = [
    upload.upload.ingested_at,
    upload.latest_run?.finished_at,
    ...upload.fields.map((field) => field.decision?.reviewed_at),
  ].filter(
    (value): value is string => typeof value === "string" && value !== "",
  );

  return timestamps.sort(
    (left, right) => dateValue(right) - dateValue(left),
  )[0];
}

function dateValue(value?: string) {
  if (!value) {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value?: string) {
  if (!value) {
    return "Unknown";
  }

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
  }).format(new Date(value));
}
